/**
 * RankHistoryChart — Line chart showing rank over time (inverted Y-axis).
 */
import { useMemo } from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import type { RankSnapshot } from '@/lib/rankHistory';

interface Props {
  history: RankSnapshot[];
  height?: number;
}

export default function RankHistoryChart({ history, height = 120 }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const { width: screenWidth } = useWindowDimensions();
  const chartWidth = Math.min(screenWidth - 48, 560);
  const padding = 10;
  const innerWidth = chartWidth - padding * 2;
  const innerHeight = height - 36;

  const { points, summary, xLabels } = useMemo(() => {
    if (history.length < 2) return { points: [], summary: null, xLabels: [] };

    const ranks = history.map((h) => h.rank);
    const max = Math.max(...ranks);
    const min = Math.min(...ranks);
    const range = max - min || 1;

    // Inverted: lower rank = higher on chart
    const pts = history.map((h, i) => ({
      x: padding + (i / (history.length - 1)) * innerWidth,
      y: 16 + ((h.rank - min) / range) * (innerHeight - 10),
    }));

    // Summary: compare last two entries
    const recent = history[history.length - 1];
    const prev = history.length >= 7
      ? history[Math.max(0, history.length - 7)]
      : history[0];

    const summ = {
      currentRank: recent.rank,
      previousRank: prev.rank,
      improved: recent.rank < prev.rank,
      same: recent.rank === prev.rank,
    };

    const fmt = (d: string) => {
      const date = new Date(d);
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    };
    const labels: { x: number; label: string }[] = [
      { x: pts[0].x, label: fmt(history[0].date) },
    ];
    if (history.length >= 2) {
      labels.push({ x: pts[pts.length - 1].x, label: fmt(history[history.length - 1].date) });
    }

    return { points: pts, summary: summ, xLabels: labels };
  }, [history, innerWidth, innerHeight]);

  if (history.length < 2) {
    return (
      <View style={styles.container}>
        <Text style={[styles.emptyText, { color: c.textTertiary }]}>
          Need more data points to show rank history
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Summary */}
      {summary && !summary.same && (
        <Text style={[styles.summary, { color: summary.improved ? c.success : c.error }]}>
          You were #{summary.previousRank} → #{summary.currentRank}
          {summary.improved ? ' ▲' : ' ▼'}
        </Text>
      )}
      {summary?.same && (
        <Text style={[styles.summary, { color: c.textSecondary }]}>
          Holding steady at #{summary.currentRank}
        </Text>
      )}

      <View style={[styles.chartArea, { width: chartWidth, height }]}>
        {/* Lines */}
        {points.map((pt, i) => {
          if (i === 0) return null;
          const prev = points[i - 1];
          const dx = pt.x - prev.x;
          const dy = pt.y - prev.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx) * (180 / Math.PI);
          return (
            <View
              key={`line-${i}`}
              style={{
                position: 'absolute',
                left: prev.x,
                top: prev.y - 1,
                width: length,
                height: 2,
                backgroundColor: c.accent,
                borderRadius: 1,
                transform: [{ rotate: `${angle}deg` }],
                transformOrigin: 'left center',
              }}
            />
          );
        })}

        {/* Dots */}
        {points.map((pt, i) => {
          const isLast = i === points.length - 1;
          const size = isLast ? 7 : 4;
          return (
            <View
              key={`dot-${i}`}
              style={{
                position: 'absolute',
                left: pt.x - size / 2,
                top: pt.y - size / 2,
                width: size,
                height: size,
                borderRadius: size / 2,
                backgroundColor: isLast ? c.accent : c.accent + '80',
              }}
            />
          );
        })}

        {/* X-axis labels */}
        {xLabels.map((label, i) => (
          <Text
            key={`xl-${i}`}
            style={{
              position: 'absolute',
              left: label.x - 24,
              bottom: 0,
              fontSize: 11,
              color: c.textTertiary,
              width: 48,
              textAlign: 'center',
            }}
          >
            {label.label}
          </Text>
        ))}

        {/* Y-axis hint */}
        <Text style={{ position: 'absolute', right: 0, top: 4, fontSize: 11, color: c.textTertiary }}>
          ↑ Better
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
  },
  summary: {
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 6,
  },
  chartArea: {
    position: 'relative',
  },
  emptyText: {
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
    paddingVertical: 8,
  },
});
